// Coupon admin CRUD page — /admin/coupons (manager+)
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X } from "lucide-react";

interface Coupon {
  id: string; code: string; type: string; value: string;
  minOrderAmount: string; usageLimit?: number; usedCount: number;
  isActive: boolean; expiresAt?: string; startsAt?: string;
  createdAt: string;
}

interface FormState {
  code: string; type: string; value: string; minOrderAmount: string;
  usageLimit: string; isActive: boolean; expiresAt: string;
}

const emptyForm: FormState = {
  code: "", type: "percent", value: "", minOrderAmount: "0",
  usageLimit: "", isActive: true, expiresAt: "",
};

function fmtVND(n: string) {
  return new Intl.NumberFormat("vi-VN").format(parseFloat(n) || 0) + " ₫";
}

export function CouponsPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formError, setFormError] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["coupons"],
    queryFn: () => apiGet<Coupon[]>("/v1/coupons?limit=50"),
  });

  const coupons = data ?? [];

  const createMut = useMutation({
    mutationFn: () =>
      apiPost("/v1/coupons", {
        code: form.code,
        type: form.type,
        value: form.value,
        minOrderAmount: form.minOrderAmount || "0",
        usageLimit: form.usageLimit ? parseInt(form.usageLimit) : undefined,
        isActive: form.isActive,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["coupons"] });
      setShowForm(false);
      setForm(emptyForm);
    },
    onError: (err: unknown) => setFormError(translateApiError(err)),
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiPatch(`/v1/coupons/${id}`, { isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["coupons"] }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiDelete(`/v1/coupons/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["coupons"] }),
  });

  const set = (k: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? (e.target as HTMLInputElement).checked : e.target.value }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Mã giảm giá</h1>
        <Button onClick={() => { setShowForm(true); setForm(emptyForm); setFormError(""); }}>
          + Tạo coupon
        </Button>
      </div>

      {showForm && (
        <div className="rounded-lg border bg-card p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">Coupon mới</h2>
            <Button variant="ghost" size="icon" onClick={() => setShowForm(false)}><X size={16} /></Button>
          </div>
          {formError && <p className="rounded bg-destructive/10 px-3 py-2 text-sm text-destructive">{formError}</p>}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium">Mã code *</label>
              <Input value={form.code} onChange={set("code")} placeholder="SALE10" required />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Loại *</label>
              <select value={form.type} onChange={set("type")} className="w-full rounded-md border bg-background px-3 py-2 text-sm">
                <option value="percent">Phần trăm (%)</option>
                <option value="fixed">Số tiền cố định (₫)</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Giá trị *</label>
              <Input type="number" value={form.value} onChange={set("value")} placeholder={form.type === "percent" ? "10" : "50000"} min={0} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Đơn tối thiểu (₫)</label>
              <Input type="number" value={form.minOrderAmount} onChange={set("minOrderAmount")} placeholder="0" min={0} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Giới hạn sử dụng</label>
              <Input type="number" value={form.usageLimit} onChange={set("usageLimit")} placeholder="Không giới hạn" min={1} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Hết hạn</label>
              <Input type="datetime-local" value={form.expiresAt} onChange={set("expiresAt")} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="isActive" checked={form.isActive} onChange={set("isActive")} />
            <label htmlFor="isActive" className="text-sm">Kích hoạt ngay</label>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowForm(false)}>Hủy</Button>
            <Button onClick={() => createMut.mutate()} disabled={createMut.isPending}>
              {createMut.isPending ? "Đang tạo..." : "Tạo coupon"}
            </Button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/50">
            <tr>
              {["Mã", "Loại", "Giá trị", "Đơn tối thiểu", "Đã dùng", "Trạng thái", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-left font-medium text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Đang tải...</td></tr>}
            {!isLoading && coupons.length === 0 && (
              <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Chưa có coupon</td></tr>
            )}
            {coupons.map((c) => (
              <tr key={c.id} className="border-b last:border-0 hover:bg-muted/30">
                <td className="px-4 py-3 font-mono font-bold">{c.code}</td>
                <td className="px-4 py-3">{c.type === "percent" ? "%" : "₫ cố định"}</td>
                <td className="px-4 py-3">
                  {c.type === "percent" ? `${c.value}%` : fmtVND(c.value)}
                </td>
                <td className="px-4 py-3">{fmtVND(c.minOrderAmount)}</td>
                <td className="px-4 py-3">
                  {c.usedCount}{c.usageLimit ? `/${c.usageLimit}` : ""}
                </td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    c.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                  }`}>
                    {c.isActive ? "Hoạt động" : "Tắt"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button
                      onClick={() => toggleMut.mutate({ id: c.id, isActive: !c.isActive })}
                      className="text-xs text-primary hover:underline"
                    >
                      {c.isActive ? "Tắt" : "Bật"}
                    </button>
                    <button
                      onClick={() => { if (confirm(`Xóa coupon ${c.code}?`)) deleteMut.mutate(c.id); }}
                      className="text-xs text-destructive hover:underline"
                    >
                      Xóa
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
